import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  InputAdornment,
  Skeleton,
  Paper,
  MenuItem,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Edit,
  Refresh,
  EmojiEvents,
  Phone,
  Email,
  CheckCircle,
  Pending,
  FilterList,
  Send,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface Winner {
  userId: string;
  username: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  photoURL: string;
  age: number | null;
  rank: number;
  monthYear: string;
  rewardAmount: number;
  badge: string;
  rewardStatus: 'pending' | 'contacted' | 'delivered';
  rewardDeliveredAt: Date | null;
  rewardCode: string;
  contactStatus: string;
  adminNotes: string;
  notificationSent: boolean;
  notificationSentAt: Date | null;
}

const WinnersManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [winners, setWinners] = useState<Winner[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState<Winner | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({
    title: '',
    body: ''
  });

  // Filters
  const [filterMonth, setFilterMonth] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRank, setFilterRank] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    status: 'pending' as 'pending' | 'contacted' | 'delivered',
    giftCardCode: '',
    contactStatus: '',
    adminNotes: '',
  });

  useEffect(() => {
    fetchWinners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMonth, filterStatus, filterRank]);

  const fetchWinners = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const params = new URLSearchParams();
      if (filterMonth) params.append('month', filterMonth);
      if (filterStatus) params.append('status', filterStatus);
      if (filterRank) params.append('rank', filterRank);

      const response = await axios.get(
        `${API_BASE_URL}/api/game/winners-management?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setWinners(response.data.data.winners || []);
      }
    } catch (error) {
      console.error('Error fetching winners:', error);
      alert('Kazananlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReward = async () => {
    if (!selectedWinner) return;

    try {
      const token = localStorage.getItem('adminToken');

      await axios.put(
        `${API_BASE_URL}/api/game/winners/${selectedWinner.userId}/${selectedWinner.monthYear}/reward`,
        formData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSuccessMessage('Ödül durumu başarıyla güncellendi!');
      setEditDialogOpen(false);
      setSelectedWinner(null);
      fetchWinners();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating reward:', error);
      alert('Ödül durumu güncellenirken hata oluştu');
    }
  };

  const openNotificationDialog = (winner: Winner) => {
    setSelectedWinner(winner);

    // Get month name
    const monthDate = new Date(winner.monthYear + '-01');
    const monthName = monthDate.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });

    // Get rank emoji and text
    const rankEmoji = winner.rank === 1 ? '🥇' : winner.rank === 2 ? '🥈' : '🥉';
    const rankText = winner.rank === 1 ? 'şampiyon' : winner.rank === 2 ? '2.' : '3.';

    // Set default message
    setNotificationMessage({
      title: `${rankEmoji} Tebrikler! Kazandınız!`,
      body: `${monthName} ayı kelime oyununda ${rankText} oldunuz! ${winner.rewardAmount}₺ hediye kartınız için Instagram'dan @sobcof hesabımıza yazın 🎁`
    });

    setNotificationDialogOpen(true);
  };

  const handleSendNotification = async () => {
    if (!selectedWinner) return;

    try {
      const token = localStorage.getItem('adminToken');

      await axios.post(
        `${API_BASE_URL}/api/game/winners/${selectedWinner.userId}/notify`,
        {
          rank: selectedWinner.rank,
          monthYear: selectedWinner.monthYear,
          rewardAmount: selectedWinner.rewardAmount,
          customTitle: notificationMessage.title,
          customBody: notificationMessage.body
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSuccessMessage('Bildirim başarıyla gönderildi!');
      setNotificationDialogOpen(false);
      fetchWinners();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error sending notification:', error);
      const errMsg = error.response?.data?.message || error.response?.data?.error || 'Bildirim gönderilirken hata oluştu';
      alert(errMsg);
    }
  };

  const openEditDialog = (winner: Winner) => {
    setSelectedWinner(winner);
    setFormData({
      status: winner.rewardStatus,
      giftCardCode: winner.rewardCode,
      contactStatus: winner.contactStatus,
      adminNotes: winner.adminNotes,
    });
    setEditDialogOpen(true);
  };

  const filteredWinners = winners.filter(w =>
    w.username.toLowerCase().includes(search.toLowerCase()) ||
    w.fullName.toLowerCase().includes(search.toLowerCase()) ||
    w.email.toLowerCase().includes(search.toLowerCase()) ||
    w.phoneNumber.includes(search)
  );

  const paginatedWinners = filteredWinners.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getRankBadgeColor = (rank: number) => {
    switch (rank) {
      case 1: return '#FFD700'; // Gold
      case 2: return '#C0C0C0'; // Silver
      case 3: return '#CD7F32'; // Bronze
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'contacted': return 'warning';
      case 'pending': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'delivered': return 'Teslim Edildi';
      case 'contacted': return 'İletişimde';
      case 'pending': return 'Bekliyor';
      default: return status;
    }
  };

  // Get unique months for filter
  const uniqueMonths = Array.from(new Set(winners.map(w => w.monthYear))).sort().reverse();

  // Stats
  const stats = {
    total: winners.length,
    pending: winners.filter(w => w.rewardStatus === 'pending').length,
    delivered: winners.filter(w => w.rewardStatus === 'delivered').length,
    totalAmount: winners.reduce((sum, w) => sum + w.rewardAmount, 0),
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#8B4513' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <EmojiEvents sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Kazananlar Yönetimi
          </Typography>
          <IconButton color="inherit" onClick={fetchWinners}>
            <Refresh />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Success Message */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
          <Card sx={{ p: 2, bgcolor: '#fff3e0' }}>
            <Typography variant="h4" color="primary">{stats.total}</Typography>
            <Typography variant="body2" color="textSecondary">Toplam Kazanan</Typography>
          </Card>
          <Card sx={{ p: 2, bgcolor: '#ffebee' }}>
            <Typography variant="h4" color="error">{stats.pending}</Typography>
            <Typography variant="body2" color="textSecondary">Bekleyen Ödül</Typography>
          </Card>
          <Card sx={{ p: 2, bgcolor: '#e8f5e9' }}>
            <Typography variant="h4" color="success.main">{stats.delivered}</Typography>
            <Typography variant="body2" color="textSecondary">Teslim Edildi</Typography>
          </Card>
          <Card sx={{ p: 2, bgcolor: '#f3e5f5' }}>
            <Typography variant="h4" color="secondary">{stats.totalAmount} ₺</Typography>
            <Typography variant="body2" color="textSecondary">Toplam Ödül Değeri</Typography>
          </Card>
        </Box>

        {/* Filters and Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 300px' }}>
              <TextField
                fullWidth
                placeholder="Kullanıcı ara (isim, email, telefon)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 150px' }}>
              <FormControl fullWidth>
                <InputLabel>Ay</InputLabel>
                <Select
                  value={filterMonth}
                  label="Ay"
                  onChange={(e) => setFilterMonth(e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {uniqueMonths.map((month) => (
                    <MenuItem key={month} value={month}>
                      {new Date(month + '-01').toLocaleDateString('tr-TR', {
                        year: 'numeric',
                        month: 'long'
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 150px' }}>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={filterStatus}
                  label="Durum"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="pending">Bekliyor</MenuItem>
                  <MenuItem value="contacted">İletişimde</MenuItem>
                  <MenuItem value="delivered">Teslim Edildi</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 150px' }}>
              <FormControl fullWidth>
                <InputLabel>Sıra</InputLabel>
                <Select
                  value={filterRank}
                  label="Sıra"
                  onChange={(e) => setFilterRank(e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="1">🥇 1. Sıra</MenuItem>
                  <MenuItem value="2">🥈 2. Sıra</MenuItem>
                  <MenuItem value="3">🥉 3. Sıra</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 180px' }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setFilterMonth('');
                  setFilterStatus('');
                  setFilterRank('');
                  setSearch('');
                }}
              >
                Filtreleri Temizle
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Winners Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Kullanıcı</strong></TableCell>
                <TableCell><strong>İletişim</strong></TableCell>
                <TableCell><strong>Ay-Yıl</strong></TableCell>
                <TableCell><strong>Sıra</strong></TableCell>
                <TableCell><strong>Ödül</strong></TableCell>
                <TableCell><strong>Durum</strong></TableCell>
                <TableCell><strong>Kart Kodu</strong></TableCell>
                <TableCell align="right"><strong>İşlemler</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))
              ) : paginatedWinners.length > 0 ? (
                paginatedWinners.map((winner) => (
                  <TableRow key={`${winner.userId}-${winner.monthYear}`} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={winner.photoURL} sx={{ width: 40, height: 40 }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {winner.fullName || winner.username}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {winner.age ? `${winner.age} yaş` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption">{winner.email || '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption">{winner.phoneNumber || '-'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(winner.monthYear + '-01').toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long'
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${winner.rank === 1 ? '🥇' : winner.rank === 2 ? '🥈' : '🥉'} ${winner.badge}`}
                        sx={{
                          bgcolor: getRankBadgeColor(winner.rank),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {winner.rewardAmount} TL
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(winner.rewardStatus)}
                        color={getStatusColor(winner.rewardStatus)}
                        size="small"
                        icon={winner.rewardStatus === 'delivered' ? <CheckCircle /> : <Pending />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {winner.rewardCode || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          color={winner.notificationSent ? 'success' : 'warning'}
                          onClick={() => openNotificationDialog(winner)}
                          title={winner.notificationSent ? 'Bildirim gönderildi - Tekrar gönder' : 'Bildirim gönder'}
                        >
                          <Send fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEditDialog(winner)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Kazanan bulunamadı
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredWinners.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Ödül Durumu Güncelle - {selectedWinner?.fullName || selectedWinner?.username}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* User Info */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Email</Typography>
                  <Typography variant="body2">{selectedWinner?.email || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Telefon</Typography>
                  <Typography variant="body2">{selectedWinner?.phoneNumber || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Ay-Yıl</Typography>
                  <Typography variant="body2">
                    {selectedWinner && new Date(selectedWinner.monthYear + '-01').toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long'
                    })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Ödül Miktarı</Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {selectedWinner?.rewardAmount} TL
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Form */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Durum</InputLabel>
              <Select
                value={formData.status}
                label="Durum"
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <MenuItem value="pending">Bekliyor</MenuItem>
                <MenuItem value="contacted">İletişimde</MenuItem>
                <MenuItem value="delivered">Teslim Edildi</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Hediye Kartı Kodu / Referans No"
              value={formData.giftCardCode}
              onChange={(e) => setFormData({ ...formData, giftCardCode: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="SB-XXXX-XXXX-XXXX"
            />

            <TextField
              fullWidth
              label="İletişim Durumu"
              value={formData.contactStatus}
              onChange={(e) => setFormData({ ...formData, contactStatus: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="Örn: WhatsApp'tan ulaşıldı, mail gönderildi"
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Admin Notları"
              value={formData.adminNotes}
              onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
              placeholder="Ödül teslim detayları, kullanıcı ile yapılan görüşme notları vb."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
          <Button onClick={handleUpdateReward} variant="contained" color="primary">
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Dialog */}
      <Dialog open={notificationDialogOpen} onClose={() => setNotificationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Send color="primary" />
            Bildirim Gönder - {selectedWinner?.fullName || selectedWinner?.username}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Mesajı istediğiniz gibi düzenleyebilirsiniz. Değişiklikler sadece bu gönderim için geçerlidir.
            </Alert>

            <TextField
              fullWidth
              label="Bildirim Başlığı"
              value={notificationMessage.title}
              onChange={(e) => setNotificationMessage({ ...notificationMessage, title: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 100 }}
              helperText={`${notificationMessage.title.length}/100`}
            />

            <TextField
              fullWidth
              multiline
              rows={5}
              label="Bildirim Mesajı"
              value={notificationMessage.body}
              onChange={(e) => setNotificationMessage({ ...notificationMessage, body: e.target.value })}
              inputProps={{ maxLength: 500 }}
              helperText={`${notificationMessage.body.length}/500`}
            />

            {/* Preview */}
            <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                Önizleme:
              </Typography>
              <Box sx={{ mt: 1, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    <Send />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {notificationMessage.title || 'Bildirim Başlığı'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {notificationMessage.body || 'Bildirim mesajı buraya gelecek...'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationDialogOpen(false)}>İptal</Button>
          <Button
            onClick={handleSendNotification}
            variant="contained"
            color="primary"
            startIcon={<Send />}
            disabled={!notificationMessage.title || !notificationMessage.body}
          >
            Gönder
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WinnersManagementPage;
